import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TICKETS } from "@/constants/testIds/tickets";
import { timeAgo, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, MagnifyingGlass, ListBullets, Kanban, Sparkle, Trash, ArrowCounterClockwise, BookmarkSimple, PencilSimple, Check, X as XIcon } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const STATUS_COLS = [
  { key: "new", label: "New" },
  { key: "waiting", label: "Waiting on Requester" },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
];

function AssigneeDot({ user }) {
  if (!user) return <span className="text-[11px] text-slate-500 font-mono">unassigned</span>;
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] shrink-0">
        {user.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" /> : initials(user.name)}
      </div>
      <span className="text-xs text-slate-700 truncate">{user.name}</span>
    </div>
  );
}

/**
 * TicketAssigneeDisplay — picks between the single-avatar display and
 * the stacked IT/Systems pair for Both-queue tickets. Falls back to
 * the legacy single-assignee shape when `assignees_by_team` is missing
 * (older tickets or those still single-team).
 *
 * Stacking rule: avatars overlap slightly with the team's accent ring
 * so you can tell at a glance which badge is IT vs Systems. If a slot
 * is unassigned we render a dashed placeholder so the "half-covered"
 * state is visible without opening the ticket.
 */
function TicketAssigneeDisplay({ ticket, userMap }) {
  const teams = Array.isArray(ticket.teams) ? ticket.teams : [];
  const abt = ticket.assignees_by_team || {};
  const hasDual = teams.length > 1 && (abt.it !== undefined || abt.systems !== undefined);
  if (!hasDual) {
    const assignee = ticket.assignee_id ? userMap[ticket.assignee_id] : null;
    return <AssigneeDot user={assignee} />;
  }
  // Teams array order matters — stack avatars left→right in that order.
  const slots = teams.map((team) => {
    const uid = abt[team] || null;
    const user = uid ? userMap[uid] : null;
    const ringClass = team === "it"
      ? "ring-[#39A36E]/60"
      : "ring-[#4f4c90]/60";
    return { team, user, ringClass };
  });
  return (
    <div className="flex items-center gap-1 min-w-0" data-testid={`dual-assignees-${ticket.id}`}>
      <div className="flex -space-x-2 shrink-0">
        {slots.map(({ team, user, ringClass }) => (
          <div
            key={team}
            title={`${team === "it" ? "IT" : "Systems"} lead: ${user?.name || "unassigned"}`}
            className={`w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[9px] ring-2 ${ringClass} ring-offset-[1px] ring-offset-white ${user ? "" : "border border-dashed border-slate-300 text-slate-400"}`}
            data-testid={`dual-assignee-${team}`}
          >
            {user ? (
              user.avatar_url
                ? <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                : initials(user.name)
            ) : (
              <span className="text-[10px] font-mono">—</span>
            )}
          </div>
        ))}
      </div>
      <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider truncate">
        {slots.map((s) => s.team.toUpperCase()).join(" + ")}
      </span>
    </div>
  );
}

function TicketCard({ t, userMap, draggable, onDragStart, onDragEnd }) {
  return (
    <Link to={`/tickets/${t.id}`} data-testid={TICKETS.row}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`block hairline rounded-md p-3 bg-white hover:bg-slate-50 transition-colors duration-100 cursor-pointer ${t.has_unread ? "ring-1 ring-[#39A36E]/40 bg-emerald-50/40" : ""}`}>
      <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
        {t.has_unread && <span className="w-1.5 h-1.5 rounded-full bg-[#39A36E] animate-pulse" aria-hidden="true" />}
        <span>#{t.number}</span>
        <span>·</span>
        <span>{timeAgo(t.created_at)}</span>
        {t.has_unread && <span className="chip !text-[#39A36E] !border-[#39A36E]/40 bg-[#39A36E]/10 !text-[10px] !py-0 ml-auto">New reply</span>}
      </div>
      <div className={`text-sm mt-1 truncate text-[#263E4F] ${t.has_unread ? "font-semibold" : "font-medium"}`}>{t.subject}</div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <span className={`chip status-${t.status}`}>{t.status.replace("_", " ")}</span>
        <span className={`chip prio-${t.priority}`}>{t.priority}</span>
        {t.system && <span className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10">{t.system}</span>}
        {t.awaiting_details && <span className="chip !bg-amber-50 !text-amber-800 !border-amber-200 !text-[10px]" data-testid={`awaiting-details-chip-${t.id}`}>Awaiting details</span>}
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100">
        <TicketAssigneeDisplay ticket={t} userMap={userMap} />
      </div>
    </Link>
  );
}

function NewTicketDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    subject: "", description: "", requester_name: "", requester_email: "", priority: "medium",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.subject || !form.description || !form.requester_email) {
      return toast.error("Fill subject, description and requester email");
    }
    setSaving(true);
    try {
      const r = await api.post("/tickets", form);
      toast.success(`Ticket #${r.data.number} created`);
      setOpen(false);
      setForm({ subject: "", description: "", requester_name: "", requester_email: "", priority: "medium" });
      onCreated?.();
    } catch {
      toast.error("Failed to create ticket");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={TICKETS.newTicketButton} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1">
          <Plus size={16} weight="bold" /> New ticket
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={TICKETS.createDialog} className="bg-white border-slate-200 max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">New ticket</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-700">Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}
              className="mt-1 bg-[#FFFFFF] border-slate-200" placeholder="Short summary" />
          </div>
          <div>
            <Label className="text-xs text-slate-700">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
              className="mt-1 bg-[#FFFFFF] border-slate-200 min-h-24" placeholder="Describe the problem" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-700">Requester name</Label>
              <Input value={form.requester_name} onChange={(e) => setForm({...form, requester_name: e.target.value})}
                className="mt-1 bg-[#FFFFFF] border-slate-200" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs text-slate-700">Requester email</Label>
              <Input value={form.requester_email} onChange={(e) => setForm({...form, requester_email: e.target.value})}
                className="mt-1 bg-[#FFFFFF] border-slate-200" placeholder="jane@company.com" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-700">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
              <SelectTrigger className="mt-1 bg-[#FFFFFF] border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {["low", "medium", "high", "urgent"].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1">
            <Sparkle size={12} weight="fill" /> AI will auto-categorize on submit
          </div>
        </div>
        <DialogFooter>
          <Button data-testid={TICKETS.createSubmit} onClick={submit} disabled={saving}
            className="bg-[#4f4c90] hover:bg-[#5F5CA5]">
            {saving ? "Creating…" : "Create ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TicketsList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter persistence — restore last-used filters when the URL has no explicit
  // filter params (i.e. the user navigated to /tickets without a deep-link).
  const LS_KEY = `hd_tickets_filters_${user?.id || "anon"}`;
  const urlHasAny = ["q", "status", "priority", "system", "assignee", "region", "location", "issue_type", "open_only", "tab"]
    .some((k) => searchParams.has(k));
  const persisted = (() => {
    if (urlHasAny) return {};
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
  })();
  const pick = (k, fallback = "all") => searchParams.get(k) ?? persisted[k] ?? fallback;

  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState(pick("q", ""));
  const [status, setStatus] = useState(pick("status"));
  const [priority, setPriority] = useState(pick("priority"));
  const [system, setSystem] = useState(pick("system"));
  const [assigneeFilter, setAssigneeFilter] = useState(pick("assignee"));
  const [region, setRegion] = useState(pick("region"));
  const [location, setLocation] = useState(pick("location"));
  const [issueType, setIssueType] = useState(pick("issue_type"));
  const [openOnly, setOpenOnly] = useState(
    searchParams.get("open_only") === "1" || (!urlHasAny && persisted.open_only === true)
  );
  const [awaitingOnly, setAwaitingOnly] = useState(
    searchParams.get("awaiting_details") === "1" || (!urlHasAny && persisted.awaiting_details === true)
  );
  const [tab, setTab] = useState(pick("tab", "list"));
  const [dragging, setDragging] = useState(null);   // ticket id being dragged
  const [dragOver, setDragOver] = useState(null);   // status column hovered
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  // §5 — Work-type view filter. Default `issue` hides Comms tickets
  // from the queue. Persisted via URL param for shareable links.
  const [workTypeView, setWorkTypeView] = useState(pick("work_type", "issue"));
  // Feb 2026 — explicit sort override. When set, overrides the backend's
  // default (number desc, unread first). Click the "Created" header to
  // toggle direction. Persisted per-user alongside the other filters so
  // "newest / oldest first" sticks across refreshes.
  const [sortKey, setSortKey] = useState(persisted.sort_key ?? null);
  const [sortDir, setSortDir] = useState(persisted.sort_dir ?? "desc");
  // §12 — team filter driven purely by URL (?teams=it or ?teams=systems).
  // Two sidebar nav items point at the two team-specific URLs; there is
  // no default landing yet — deliberate, until we add user-team.
  const teamsParam = pick("teams", "");

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const displayedTickets = useMemo(() => {
    if (!sortKey) return tickets;
    const copy = [...tickets];
    copy.sort((a, b) => {
      const av = a[sortKey] || "";
      const bv = b[sortKey] || "";
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [tickets, sortKey, sortDir]);
  const toggleSortCreated = () => {
    if (sortKey !== "created_at") {
      setSortKey("created_at");
      setSortDir("desc");
    } else {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    }
  };
  const systems = useMemo(() => {
    const set = new Set();
    tickets.forEach((t) => { if (t.system) set.add(t.system); });
    return Array.from(set).sort();
  }, [tickets]);

  const load = () => {
    const params = { status_: status, priority, q: q || undefined, system: system === "all" ? undefined : system };
    if (assigneeFilter === "unassigned") params.assignee_id = "__none__";
    else if (assigneeFilter !== "all") params.assignee_id = assigneeFilter;
    if (region !== "all") params.region = region;
    if (location !== "all") params.location = location;
    if (issueType !== "all") params.issue_type = issueType;
    if (openOnly) params.open_only = true;
    if (awaitingOnly) params.awaiting_details = true;
    if (workTypeView && workTypeView !== "issue") params.work_type = workTypeView;
    if (teamsParam) params.teams = teamsParam;
    api.get("/tickets", { params }).then((r) => {
      setTickets(r.data);
      setSelected(new Set());
    });
  };

  // Keep the URL in sync so filtered views are shareable and refresh-safe,
  // and persist the same values to localStorage so the last-used filter
  // is remembered across sessions.
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (status !== "all") next.set("status", status);
    if (priority !== "all") next.set("priority", priority);
    if (system !== "all") next.set("system", system);
    if (assigneeFilter !== "all") next.set("assignee", assigneeFilter);
    if (region !== "all") next.set("region", region);
    if (location !== "all") next.set("location", location);
    if (issueType !== "all") next.set("issue_type", issueType);
    if (openOnly) next.set("open_only", "1");
    if (awaitingOnly) next.set("awaiting_details", "1");
    if (workTypeView && workTypeView !== "issue") next.set("work_type", workTypeView);
    if (tab !== "list") next.set("tab", tab);
    // §12 — preserve the sidebar's team scope. This effect rebuilds
    // the URL from the filter state so shareable links work, but
    // `teams` is driven from the URL (not from React state), so we
    // must copy it through or the sidebar scope gets stripped on
    // the next filter change.
    const currentTeams = searchParams.get("teams");
    if (currentTeams) next.set("teams", currentTeams);
    setSearchParams(next, { replace: true });
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        q, status, priority, system, assignee: assigneeFilter,
        region, location, issue_type: issueType, open_only: openOnly, awaiting_details: awaitingOnly, tab,
        sort_key: sortKey, sort_dir: sortDir,
      }));
    } catch { /* localStorage unavailable — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, priority, system, assigneeFilter, region, location, issueType, openOnly, awaitingOnly, tab, sortKey, sortDir]);

  const clearAllFilters = () => {
    setQ(""); setStatus("all"); setPriority("all"); setSystem("all");
    setAssigneeFilter("all"); setRegion("all"); setLocation("all"); setIssueType("all"); setOpenOnly(false); setAwaitingOnly(false);
    setSortKey(null); setSortDir("desc");
    try { localStorage.removeItem(LS_KEY); } catch { /* noop */ }
  };

  // Human-readable active filter chips
  const activeChips = [];
  if (openOnly) activeChips.push({ key: "open", label: "Open only", clear: () => setOpenOnly(false) });
  if (awaitingOnly) activeChips.push({ key: "awaiting", label: "Awaiting details", clear: () => setAwaitingOnly(false) });
  if (status !== "all") activeChips.push({ key: "status", label: `Status: ${STATUS_COLS.find((s) => s.key === status)?.label || status}`, clear: () => setStatus("all") });
  if (priority !== "all") activeChips.push({ key: "priority", label: `Priority: ${priority}`, clear: () => setPriority("all") });
  if (system !== "all") activeChips.push({ key: "system", label: system === "__none__" ? "System: untagged" : `System: ${system}`, clear: () => setSystem("all") });
  if (assigneeFilter === "unassigned") activeChips.push({ key: "assignee", label: "Unassigned", clear: () => setAssigneeFilter("all") });
  else if (assigneeFilter !== "all") activeChips.push({ key: "assignee", label: `Assignee: ${userMap[assigneeFilter]?.name || assigneeFilter}`, clear: () => setAssigneeFilter("all") });
  if (region !== "all") activeChips.push({ key: "region", label: region === "__none__" ? "Region: untagged" : `Region: ${region}`, clear: () => setRegion("all") });
  if (location !== "all") activeChips.push({ key: "location", label: location === "__none__" ? "Location: untagged" : `Location: ${location}`, clear: () => setLocation("all") });
  if (issueType !== "all") activeChips.push({ key: "issue_type", label: issueType === "__none__" ? "Issue type: untagged" : `Issue: ${issueType}`, clear: () => setIssueType("all") });
  if (q) activeChips.push({ key: "q", label: `Search: "${q}"`, clear: () => setQ("") });

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === tickets.length && tickets.length > 0) return new Set();
      return new Set(tickets.map((t) => t.id));
    });
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} ticket${selected.size === 1 ? "" : "s"}? This also removes their comments & activity and cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/tickets/bulk-delete", { ticket_ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted_tickets} ticket${r.data.deleted_tickets === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  // §10 — Bulk edit. Applies a single field change to every selected
  // ticket. Whitelisted to status/priority/work_type/assignee_id/
  // system/issue_type on the backend.
  const bulkUpdate = async (patch, label) => {
    if (selected.size === 0) return;
    setBulkUpdating(true);
    try {
      const r = await api.post("/tickets/bulk-update", {
        ticket_ids: Array.from(selected),
        updates: patch,
      });
      const { updated = 0, skipped = 0 } = r.data || {};
      const msg = skipped > 0 ? `${label}: ${updated} updated, ${skipped} skipped` : `${label}: ${updated} updated`;
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkUpdating(false);
    }
  };

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const [settingsDoc, setSettingsDoc] = useState({ systems: [], regions: [] });
  useEffect(() => { api.get("/settings").then((r) => setSettingsDoc(r.data)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, priority, system, assigneeFilter, region, location, issueType, openOnly, awaitingOnly, workTypeView, teamsParam]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [q]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_COLS.map((c) => [c.key, []]));
    tickets.forEach((t) => { if (g[t.status]) g[t.status].push(t); });
    return g;
  }, [tickets]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {teamsParam === "it" ? "IT Tickets" : teamsParam === "systems" ? "Bsys Tickets" : "Tickets"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{tickets.length} ticket{tickets.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* §5 — Work type segmented pill. Top-level so agents can see
              they're on the Issues view and switch to Comms with one click. */}
          <div
            data-testid="tickets-worktype-pill"
            className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 text-xs"
          >
            {[
              { key: "issue", label: "Issues" },
              { key: "comms", label: "Comms" },
              { key: "all", label: "Both" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setWorkTypeView(opt.key)}
                data-testid={`tickets-worktype-${opt.key}`}
                className={`px-3 py-1 rounded-full transition ${
                  workTypeView === opt.key
                    ? "bg-[#4f4c90] text-white font-medium"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <NewTicketDialog onCreated={load} />
          {isAdmin && <RecycleBinButton onRestored={load} />}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid={TICKETS.searchInput} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject, email, description…"
            className="pl-9 h-9 bg-white border-slate-200 text-sm" />
        </div>
        <Select
          value={openOnly ? "__open__" : status}
          onValueChange={(v) => {
            if (v === "__open__") { setOpenOnly(true); setStatus("all"); }
            else { setOpenOnly(false); setStatus(v); }
            // Stage 4 — Kanban has no column for "withdrawn". Auto-switch
            // to list view so those tickets stay visible when filtered.
            if (v === "withdrawn" && tab === "kanban") setTab("list");
          }}
        >
          <SelectTrigger data-testid={TICKETS.statusFilter} className="w-40 h-9 bg-white border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="__open__" data-testid="status-filter-open">Open (not resolved)</SelectItem>
            {STATUS_COLS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            <SelectItem value="withdrawn" data-testid="status-filter-withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setAwaitingOnly((v) => !v)}
          data-testid="awaiting-details-toggle"
          className={`h-9 px-3 rounded-md border text-xs font-medium transition-colors ${
            awaitingOnly
              ? "bg-amber-100 text-amber-900 border-amber-300"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
        >
          {awaitingOnly ? "✓ Awaiting details" : "Awaiting details"}
        </button>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid={TICKETS.priorityFilter} className="w-32 h-9 bg-white border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="all">All priorities</SelectItem>
            {["low", "medium", "high", "urgent"].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger data-testid="tickets-assignee-filter" className="w-40 h-9 bg-white border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {users.filter((u) => u.role !== "requester").map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {systems.length > 0 && (
          <Select value={system} onValueChange={setSystem}>
            <SelectTrigger data-testid="tickets-system-filter" className="w-36 h-9 bg-white border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All systems</SelectItem>
              {systems.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <SavedFiltersMenu
          current={{ q, status, priority, system, assigneeFilter, tab, region, location, issueType, openOnly }}
          onApply={(c) => {
            setQ(c.q || "");
            setStatus(c.status || "all");
            setPriority(c.priority || "all");
            setSystem(c.system || "all");
            setAssigneeFilter(c.assigneeFilter || "all");
            setRegion(c.region || "all");
            setLocation(c.location || "all");
            setIssueType(c.issueType || "all");
            setOpenOnly(!!c.openOnly);
            setTab(c.tab || "list");
          }}
        />

        <Tabs value={tab} onValueChange={setTab} className="ml-auto">
          <TabsList className="bg-white border border-slate-200 h-9">
            <TabsTrigger value="list" data-testid={TICKETS.listView} className="data-[state=active]:bg-slate-200 gap-1 h-7">
              <ListBullets size={14} /> List
            </TabsTrigger>
            <TabsTrigger value="kanban" data-testid={TICKETS.kanbanView} className="data-[state=active]:bg-slate-200 gap-1 h-7">
              <Kanban size={14} /> Kanban
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {activeChips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="tickets-active-filter-bar">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Active filters:</span>
          {activeChips.map((c) => (
            <button
              key={c.key}
              onClick={c.clear}
              className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 hover:bg-[#4f4c90]/20 !text-xs !py-0.5 gap-1"
              data-testid={`filter-chip-${c.key}`}
              title="Remove this filter"
            >
              {c.label} <XIcon size={11} />
            </button>
          ))}
          <button
            onClick={clearAllFilters}
            className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
            data-testid="filter-clear-all"
          >Clear all</button>
        </div>
      )}

      {tab === "list" ? (
        <div className="hairline rounded-lg overflow-hidden bg-white">
          {selected.size > 0 && (
            <div
              data-testid="tickets-bulk-actionbar"
              className="flex items-center justify-between px-4 py-2 bg-[#4f4c90]/10 border-b border-[#4f4c90]/30 text-sm"
            >
              <div className="text-[#4f4c90] font-medium">
                {selected.size} selected
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="h-8 border-slate-200 bg-white text-xs"
                  onClick={() => setSelected(new Set())}
                  disabled={bulkDeleting || bulkUpdating}
                  data-testid="tickets-bulk-clear"
                >
                  Clear
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-8 border-slate-200 bg-white text-xs gap-1"
                      disabled={bulkUpdating || bulkDeleting}
                      data-testid="tickets-bulk-edit-trigger"
                    >
                      <PencilSimple size={13} /> {bulkUpdating ? "Updating…" : "Edit"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white">
                    <DropdownMenuLabel>Set status</DropdownMenuLabel>
                    {[
                      { key: "new", label: "New" },
                      { key: "in_progress", label: "In progress" },
                      { key: "waiting", label: "Waiting on requester" },
                      { key: "resolved", label: "Resolved" },
                    ].map((s) => (
                      <DropdownMenuItem
                        key={s.key}
                        data-testid={`tickets-bulk-status-${s.key}`}
                        onClick={() => bulkUpdate({ status: s.key }, `Status → ${s.label}`)}
                      >
                        {s.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Set priority</DropdownMenuLabel>
                    {["low", "medium", "high", "urgent"].map((p) => (
                      <DropdownMenuItem
                        key={p}
                        data-testid={`tickets-bulk-priority-${p}`}
                        className="capitalize"
                        onClick={() => bulkUpdate({ priority: p }, `Priority → ${p}`)}
                      >
                        {p}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Work type</DropdownMenuLabel>
                    <DropdownMenuItem
                      data-testid="tickets-bulk-worktype-issue"
                      onClick={() => bulkUpdate({ work_type: "issue" }, "Marked as Issue")}
                    >
                      Issue (visible in queue)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="tickets-bulk-worktype-comms"
                      onClick={() => bulkUpdate({ work_type: "comms" }, "Marked as Comms")}
                    >
                      Comms (hidden from queue)
                    </DropdownMenuItem>
                    {(settingsDoc.systems || []).length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Set system</DropdownMenuLabel>
                        <DropdownMenuItem
                          data-testid="tickets-bulk-system-clear"
                          onClick={() => bulkUpdate({ system: null }, "System cleared")}
                          className="text-slate-500"
                        >
                          — clear —
                        </DropdownMenuItem>
                        {(settingsDoc.systems || []).slice(0, 30).map((s) => (
                          <DropdownMenuItem
                            key={s}
                            data-testid={`tickets-bulk-system-${s}`}
                            onClick={() => bulkUpdate({ system: s }, `System → ${s}`)}
                          >
                            {s}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {(settingsDoc.regions || []).length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Set region</DropdownMenuLabel>
                        <DropdownMenuItem
                          data-testid="tickets-bulk-region-clear"
                          onClick={() => bulkUpdate({ region: null, location: null }, "Region cleared")}
                          className="text-slate-500"
                        >
                          — clear —
                        </DropdownMenuItem>
                        {(settingsDoc.regions || []).map((r) => (
                          <DropdownMenuItem
                            key={r.name}
                            data-testid={`tickets-bulk-region-${r.name}`}
                            onClick={() => bulkUpdate({ region: r.name, location: null }, `Region → ${r.name}`)}
                          >
                            {r.name}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  onClick={bulkDelete}
                  disabled={bulkDeleting || bulkUpdating}
                  data-testid="tickets-bulk-delete"
                  className={`h-8 bg-red-500/20 border border-red-500/40 text-red-600 hover:bg-red-500/30 gap-1 text-xs ${!isAdmin ? "hidden" : ""}`}
                >
                  <Trash size={13} /> {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
                </Button>
              </div>
            </div>
          )}
          <div className={`grid grid-cols-[36px_52px_1fr_110px_90px_120px_110px_150px_100px] gap-3 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 font-medium`}>
            <div className="flex items-center">
              <Checkbox
                checked={tickets.length > 0 && selected.size === tickets.length}
                onCheckedChange={toggleAll}
                aria-label="Select all"
                data-testid="tickets-select-all"
              />
            </div>
            <div>ID</div><div>Subject</div><div>Status</div><div>Priority</div><div>System</div><div>Region</div><div>Assignee</div>
            <button
              type="button"
              onClick={toggleSortCreated}
              className={`text-left uppercase tracking-wider font-medium flex items-center gap-1 hover:text-[#4f4c90] transition-colors ${sortKey === "created_at" ? "text-[#4f4c90]" : "text-slate-500"}`}
              data-testid="tickets-sort-created"
              aria-label={`Sort by created date, currently ${sortKey === "created_at" ? sortDir : "default"}`}
              title={
                sortKey === "created_at"
                  ? (sortDir === "desc" ? "Newest first (click to reverse)" : "Oldest first (click to reverse)")
                  : "Click to sort by created date"
              }
            >
              Created
              {sortKey === "created_at" && (
                <span className="font-mono text-[10px] leading-none" data-testid="tickets-sort-created-arrow">
                  {sortDir === "desc" ? "▼" : "▲"}
                </span>
              )}
            </button>
          </div>
          {displayedTickets.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No tickets match your filters.</div>}
          {displayedTickets.map((t) => {
            const isSelected = selected.has(t.id);
            return (
              <div key={t.id}
                data-testid={t.has_unread ? `ticket-unread-${t.number}` : undefined}
                className={`grid grid-cols-[36px_52px_1fr_110px_90px_120px_110px_150px_100px] gap-3 px-4 py-3 items-center row-hover border-b border-slate-100 last:border-0 ${isSelected ? "bg-[#4f4c90]/5" : ""} ${t.has_unread ? "bg-emerald-50/40" : ""}`}>
                <div
                  className="flex items-center"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleOne(t.id)}
                    aria-label={`Select ticket ${t.number}`}
                    data-testid={`tickets-select-${t.number}`}
                  />
                </div>
                <Link to={`/tickets/${t.id}`} data-testid={TICKETS.row} className="font-mono text-xs text-slate-500 flex items-center gap-1.5">
                  {t.has_unread && <span className="w-1.5 h-1.5 rounded-full bg-[#39A36E] animate-pulse" aria-hidden="true" />}
                  #{t.number}
                </Link>
                <Link to={`/tickets/${t.id}`} className="min-w-0">
                  <div className={`text-sm truncate flex items-center gap-2 ${t.has_unread ? "font-semibold text-slate-900" : ""}`}>
                    <span className="truncate">{t.subject}</span>
                    {t.has_unread && (
                      <span className="chip !text-[#39A36E] !border-[#39A36E]/40 bg-[#39A36E]/10 !text-[10px] !py-0.5 flex-shrink-0">New reply</span>
                    )}
                    {t.awaiting_details && (
                      <span
                        className="chip !bg-amber-50 !text-amber-800 !border-amber-200 !text-[10px] !py-0.5 flex-shrink-0"
                        data-testid={`awaiting-details-chip-${t.id}`}
                      >
                        Awaiting details
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{t.requester_name} · {t.requester_email}</div>
                </Link>
                <Link to={`/tickets/${t.id}`}><span className={`chip status-${t.status}`}>{t.status.replace("_"," ")}</span></Link>
                <Link to={`/tickets/${t.id}`}><span className={`chip prio-${t.priority}`}>{t.priority}</span></Link>
                <Link to={`/tickets/${t.id}`} className="min-w-0">
                  {t.system ? (
                    <span className="chip !text-[#8B87C8] !border-[#4f4c90]/30 bg-[#4f4c90]/10 truncate max-w-full">{t.system}</span>
                  ) : (
                    <span className="text-[11px] text-slate-400 font-mono">—</span>
                  )}
                </Link>
                <Link to={`/tickets/${t.id}`} className="min-w-0">
                  {t.region ? (
                    <span className="text-xs text-slate-700 truncate max-w-full block">{t.region}</span>
                  ) : (
                    <span className="text-[11px] text-slate-400 font-mono">—</span>
                  )}
                </Link>
                <Link to={`/tickets/${t.id}`} className="min-w-0"><TicketAssigneeDisplay ticket={t} userMap={userMap} /></Link>
                <Link to={`/tickets/${t.id}`} className="text-xs text-slate-500 font-mono">{timeAgo(t.created_at)}</Link>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 items-start" data-testid="kanban-board">
          {STATUS_COLS.map((col) => (
            <div
              key={col.key}
              className={`w-72 shrink-0 rounded-lg p-1 transition-colors ${dragOver === col.key ? "kanban-drop-active" : ""}`}
              onDragOver={(e) => { if (dragging) { e.preventDefault(); setDragOver(col.key); } }}
              onDragLeave={() => setDragOver((s) => (s === col.key ? null : s))}
              onDrop={async (e) => {
                e.preventDefault();
                setDragOver(null);
                if (!dragging) return;
                const t = tickets.find((x) => x.id === dragging);
                if (!t || t.status === col.key) { setDragging(null); return; }
                // Optimistic update
                setTickets((prev) => prev.map((x) => x.id === dragging ? { ...x, status: col.key } : x));
                try {
                  await api.patch(`/tickets/${dragging}`, { status: col.key });
                  toast.success(`Moved #${t.number} to ${col.label}`);
                } catch (err) {
                  toast.error("Failed to update status");
                  load();
                }
                setDragging(null);
              }}
            >
              <div className="flex items-center justify-between px-2 py-2">
                <div className="flex items-center gap-2">
                  <span className={`chip status-${col.key}`}>{col.label}</span>
                  <span className="text-xs text-slate-500 font-mono">{grouped[col.key].length}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 min-h-[80px]">
                {grouped[col.key].map((t) => (
                  <TicketCard
                    key={t.id}
                    t={t}
                    userMap={userMap}
                    draggable
                    onDragStart={(e) => {
                      setDragging(t.id);
                      e.dataTransfer.effectAllowed = "move";
                      e.currentTarget.classList.add("kanban-card-dragging");
                    }}
                    onDragEnd={(e) => {
                      setDragging(null);
                      setDragOver(null);
                      e.currentTarget.classList.remove("kanban-card-dragging");
                    }}
                  />
                ))}
                {grouped[col.key].length === 0 && <div className="text-[11px] text-slate-400 p-3 text-center border border-dashed border-slate-200 rounded-md">Drop tickets here</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecycleBinButton({ onRestored }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [restoringId, setRestoringId] = useState("");

  const refresh = () =>
    api.get("/tickets-deleted")
      .then((r) => { setItems(r.data); setCount(r.data.length); })
      .catch(() => { /* silently ignore for non-admins */ });

  useEffect(() => { refresh(); }, []);
  useEffect(() => { if (open) refresh(); }, [open]);

  const restore = async (t) => {
    setRestoringId(t.id);
    try {
      await api.post(`/tickets/${t.id}/restore`);
      toast.success(`Ticket #${t.number} restored`);
      await refresh();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoringId("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="h-9 gap-1 border-slate-200 bg-white text-slate-600 hover:text-[#4f4c90]"
          data-testid="tickets-recycle-bin-btn"
          title={count === 0 ? "Recycle bin is empty" : `${count} deleted ticket${count === 1 ? "" : "s"}`}
        >
          <Trash size={14} weight="duotone" />
          Recycle bin
          {count > 0 && (
            <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{count}</span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Trash size={16} weight="duotone" className="text-slate-600" /> Recycle bin
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500">
          Deleted tickets are hidden from the queue, dashboards, insights, and portal. Restore any of them to bring them back exactly as they were.
        </div>
        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="tickets-recycle-bin-list">
          {items.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">No deleted tickets.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {items.map((t) => (
                <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`recycle-row-${t.number}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-slate-500">#{t.number}</span>
                      <span className={`chip status-${t.status} !text-[10px]`}>{t.status.replace("_", " ")}</span>
                      <span className="text-sm truncate">{t.subject}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                      {t.requester_email} · deleted {timeAgo(t.deleted_at)}
                    </div>
                  </div>
                  <Button
                    onClick={() => restore(t)}
                    disabled={restoringId === t.id}
                    variant="outline"
                    className="h-8 border-slate-200 bg-white gap-1 text-xs"
                    data-testid={`recycle-restore-${t.number}`}
                  >
                    <ArrowCounterClockwise size={12} />
                    {restoringId === t.id ? "Restoring…" : "Restore"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => setOpen(false)} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8 text-xs">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SavedFiltersMenu({ current, onApply }) {
  const [filters, setFilters] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [renamingId, setRenamingId] = useState("");
  const [renameDraft, setRenameDraft] = useState("");

  const load = () =>
    api.get("/saved-filters").then((r) => setFilters(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // A filter is "default" (nothing to save) only when every field is unset.
  // Missing fields here would leave the Save button greyed out even when
  // valid filter criteria has been applied — keep this in sync with the
  // full list of filter fields on TicketsList.
  const isDefault =
    !current.q &&
    (!current.status || current.status === "all") &&
    (!current.priority || current.priority === "all") &&
    (!current.system || current.system === "all") &&
    (!current.assigneeFilter || current.assigneeFilter === "all") &&
    (!current.region || current.region === "all") &&
    (!current.location || current.location === "all") &&
    (!current.issueType || current.issueType === "all") &&
    !current.openOnly;

  const save = async () => {
    const name = nameDraft.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/saved-filters", { name, criteria: current });
      toast.success(`Saved "${name}"`);
      setSaveOpen(false);
      setNameDraft("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const apply = (f) => {
    onApply(f.criteria || {});
    toast.success(`Applied "${f.name}"`);
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.name}"?`)) return;
    try {
      await api.delete(`/saved-filters/${f.id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const commitRename = async (f) => {
    const n = renameDraft.trim();
    if (!n || n === f.name) { setRenamingId(""); return; }
    try {
      await api.patch(`/saved-filters/${f.id}`, { name: n });
      setRenamingId("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-9 gap-1 border-slate-200 bg-white text-slate-600 hover:text-[#4f4c90]"
            data-testid="saved-filters-btn"
          >
            <BookmarkSimple size={14} weight="duotone" />
            Saved
            {filters.length > 0 && (
              <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{filters.length}</span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white border-slate-200 w-64">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Your saved filters</DropdownMenuLabel>
          {filters.length === 0 && (
            <div className="px-2 py-3 text-xs text-slate-500 text-center italic">No saved filters yet.</div>
          )}
          {filters.map((f) => (
            <div key={f.id} className="px-2 py-1 flex items-center gap-1 group" data-testid={`saved-filter-${f.id}`}>
              {renamingId === f.id ? (
                <>
                  <Input
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(f);
                      if (e.key === "Escape") setRenamingId("");
                    }}
                    className="h-7 text-xs bg-white border-slate-200"
                    data-testid={`saved-filter-rename-input-${f.id}`}
                  />
                  <button onClick={() => commitRename(f)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Save name"><Check size={12} /></button>
                  <button onClick={() => setRenamingId("")} className="p-1 text-slate-500 hover:bg-slate-100 rounded" title="Cancel"><XIcon size={12} /></button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => apply(f)}
                    className="flex-1 text-left text-xs px-2 py-1.5 rounded hover:bg-slate-50 truncate"
                    data-testid={`saved-filter-apply-${f.id}`}
                    title={JSON.stringify(f.criteria, null, 2)}
                  >
                    {f.name}
                  </button>
                  <button
                    onClick={() => { setRenamingId(f.id); setRenameDraft(f.name); }}
                    className="p-1 text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`saved-filter-rename-${f.id}`}
                    title="Rename"
                  ><PencilSimple size={12} /></button>
                  <button
                    onClick={() => remove(f)}
                    className="p-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`saved-filter-delete-${f.id}`}
                    title="Delete"
                  ><Trash size={12} /></button>
                </>
              )}
            </div>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isDefault}
            onSelect={(e) => { e.preventDefault(); setSaveOpen(true); }}
            data-testid="saved-filter-save-current"
            className="text-xs cursor-pointer"
          >
            <Plus size={12} className="mr-1.5" /> Save current filter
            {isDefault && <span className="ml-auto text-[10px] text-slate-400">(no filters set)</span>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Save filter</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Name</Label>
            <Input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              placeholder="e.g. My open high-priority"
              className="bg-white border-slate-200"
              data-testid="saved-filter-name-input"
            />
            <div className="text-[10px] text-slate-500 font-mono truncate" title={JSON.stringify(current)}>
              {Object.entries(current).filter(([, v]) => v && v !== "all" && v !== "list").map(([k, v]) => `${k}=${v}`).join(" · ") || "no filters"}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)} className="text-slate-600 hover:text-slate-900 h-8 text-xs">Cancel</Button>
            <Button onClick={save} disabled={saving || !nameDraft.trim()} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8 text-xs" data-testid="saved-filter-save-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

